import styles from './styles.module.scss';
import { useState } from 'react';
import Typography from '@components/common/typography';
import PaymentsTable from '@sections/pay_bills/schedule_payments/payments_table';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiScheduleService } from '@api';
import { ENewNotificationType } from '@enum';
import Pagination from '@components/pagination';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { EScheduleFrequency, EScheduleStatus, TSchedule } from '@xeppt/xeppt-sdk/types/schedule';
import { ETransactionMethod } from '@xeppt/xeppt-sdk/types';
import { routes } from '@const/routes';
import Button from '@components/common/button';
import SearchInput from '@components/search';
import { useNavigate } from 'react-router-dom';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { useDebounceValue } from 'usehooks-ts';
import type { TListSchedulesFilterWithSearch } from 'src/types/reference_code';

const SchedulePaymentsSection = () => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'pay_bills.schedule_payments'
    });
    const { placeholderLocale, submitLocale } = useLocales();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebounceValue(search, 300);
    const [filter, setFilter] = useState<{
        paymentFrequency?: EScheduleFrequency[];
        status?: EScheduleStatus[];
    }>({
        paymentFrequency: [],
        status: []
    });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const { handleBack } = useNavigationContext();
    // The schedule list ORs the reference_code alias with the info field(s)
    // server-side under one `search` param (mirrors transactions' `search`) —
    // no FE routing needed. Collapse sub-3-char input to a stable dep so
    // every keystroke below the threshold doesn't trigger its own refetch.
    const hasSearch = debouncedSearch.length >= 3;
    const searchDep = hasSearch ? debouncedSearch : '';
    const {
        data: paymentsList,
        handleRequest: refetchPaymentsList,
        isLoading
    } = useApiQuery({
        method: () =>
            apiScheduleService.listSchedules(pageSize, page, {
                frequency:
                    (filter.paymentFrequency || [])?.length > 0
                        ? filter.paymentFrequency
                        : undefined,
                status: (filter.status || [])?.length > 0 ? filter.status : undefined,
                method: [ETransactionMethod.PAY_BILL],
                search: hasSearch ? debouncedSearch : undefined
            } as TListSchedulesFilterWithSearch),
        deps: [filter, page, pageSize, searchDep],
        notificationType: ENewNotificationType.BILL,
        isInitialLoading: true
    });

    return (
        <motion.div {...pageAnimation} className={styles.main_wrapper}>
            <div className={styles.wrapper}>
                <div className={styles.content}>
                    <div className={styles.header}>
                        <Button
                            className={styles.back}
                            leftIcon="arrow_left"
                            variant="text-gray"
                            onClick={() => handleBack(routes.pay_bills)}>
                            {submitLocale('back')}
                        </Button>
                        <Typography variant="h4">{t('title')}</Typography>
                    </div>
                </div>
                <div className={styles.search_row}>
                    <SearchInput
                        className={styles.search}
                        value={search}
                        onChange={(val) => {
                            setSearch(val);
                            setPage(1);
                        }}
                        placeholder={placeholderLocale('search_by_payee')}
                    />
                    <Button
                        variant="primary"
                        size="medium"
                        onClick={() => navigate(routes.pay_bills_pay)}>
                        {submitLocale('schedule_new')}
                    </Button>
                </div>
                <PaymentsTable
                    refetchPaymentsList={refetchPaymentsList}
                    paymentsList={paymentsList?.data || []}
                    onFilterChange={(filter) => setFilter(filter)}
                    filter={filter}
                    isLoading={isLoading}
                    onEdit={(data: TSchedule) => {
                        navigate(`${routes.pay_bills_pay}?id=${data.id}`);
                    }}
                />
                {(paymentsList?.pages_total || 0) > 1 && (
                    <Pagination
                        pageCount={paymentsList?.pages_total || 0}
                        onPageChange={(val) => setPage(val)}
                        pageSize={pageSize}
                        onPageSizeChange={(val) => setPageSize(val)}
                    />
                )}
            </div>
        </motion.div>
    );
};

export default SchedulePaymentsSection;
